import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  get, isNil, omitBy,
} from 'lodash';
import router from 'next/router';
import {
  IconButton, TextField, InputLabel,
} from '@mui/material';
import { useState } from 'react';
import { Post } from 'lib/types';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { Credit, DisputedCredit } from 'lib/types/credits';
import { useSignUpModal } from 'components/SignUpModal';
import { trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { CreditSelect } from './CreditSelect';

interface Props {
  post: Post;
  onChange: () => void;
}

export const CreditReport = ({
  post,
  onChange,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const { showSignUpModal } = useSignUpModal();
  const { credits } = post;

  const [selectedCredit, setSelectedCredit] = useState<Credit | null>(null);
  const [comments, setComments] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isReportingCredit, setIsReportingCredit] = useState(false);

  const reportClick = () => {
    if (currentUser && currentUser.email && credits.length > 0) {
      setIsReportingCredit(true);
    } else if (currentUser && !currentUser.email) {
      router.push({ pathname: '/settings', query: '' });
    } else {
      trackClickSignupEvent(API, 'credit-report');
      showSignUpModal('Please sign up or sign in to report a credits error');
    }
  };

  // When a user reports an incorrect credit, we post it
  const reportCredit = async (disputedCredit: DisputedCredit) => {
    setIsSaving(true);
    try {
      const payload = omitBy(disputedCredit, isNil);
      await API.reportCredit(payload as DisputedCredit);

      enqueueSnackbar('Your report has been sent!', { variant: 'success' });
    } catch (error) {
      const msg = get(error, 'message');
      enqueueSnackbar(`There was an error reporting this credit: ${msg}`, { variant: 'error' });
    }
    setIsSaving(false);
    setIsReportingCredit(false);
    onChange();
  };

  // Submitting without proposed credits
  const onSubmit = () => {
    reportCredit({
      creditId: get(selectedCredit, 'id') as number,
      comments,
    });
  };

  const canSubmitReport = !isSaving && selectedCredit;

  if (!credits.length) {
    return null;
  }

  if (!isReportingCredit) {
    return (
      <div className="text-right pt-2">
        <button type="button" className="btn-primary-outlined" onClick={reportClick}>
          {currentUser && !currentUser.email
            ? 'Add an email address to report a credits error'
            : 'Report a credits error'}
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="py-5">
        <div className="text-lg font-bold">
          Report a Credit Error
        </div>
        <div className="absolute right-2 top-2">
          <IconButton
            color="primary"
            aria-label="cancel report credit"
            onClick={() => setIsReportingCredit(false)}
            size="large"
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </div>
      </div>
      <div className="mb-4">
        <InputLabel htmlFor="credit-select" className="mb-2">
          Which credit contains an error?
        </InputLabel>

        <CreditSelect
          credits={credits.filter((c) => !c.isHidden)}
          onChange={setSelectedCredit}
          value={selectedCredit}
        />
      </div>
      <div>
        <InputLabel htmlFor="credit-notes" className="mb-2">
          What is wrong with the credit?
        </InputLabel>

        <TextField
          rows={2}
          onChange={(e) => setComments(e.target.value)}
          autoFocus
          multiline
          inputProps={{ maxLength: 1000 }}
          id="credit-notes"
          variant="outlined"
          fullWidth
          label="Please explain in detail,
          and let us know the correct credit if you know it."
        />
      </div>
      <div className="flex justify-end pb-2 my-4 border-b">
        <button
          type="button"
          className="btn-primary-outlined mr-2"
          onClick={() => setIsReportingCredit(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!canSubmitReport}
          onClick={onSubmit}
        >
          {isSaving ? 'Sending...' : 'Send Report'}
        </button>
      </div>
    </div>
  );
};
